// Local-first data layer for the rundown (local-first migration, Phase 3).
//
// CRUD reads/writes for cues + shows run against the local PowerSync SQLite
// store: instant, offline-capable, and uploaded to Neon in the background by
// the connector. Returns axios-shaped `{ data }` so lib/api.js can delegate to
// these transparently when VITE_LOCAL_FIRST is on, leaving call sites unchanged.
//
// NOT here (stay on REST — server orchestration, not data): take/skip/back,
// vMix/Singular control, CSV/Sheets import, show start/end/reset/clone.
import { getPowerSync } from './db';

const parseJson = (v, fallback) => {
  if (v === null || v === undefined) return fallback;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return fallback; }
};

// SQLite row → API-shaped cue object.
function rowToCue(row) {
  if (!row) return null;
  return {
    ...row,
    enabled: !!row.enabled,
    cue_actions: parseJson(row.cue_actions, []),
    vmix_actions: parseJson(row.vmix_actions, []),
    singular_action: parseJson(row.singular_action, null),
  };
}

// API-shaped cue field → SQLite-storable value.
function toCol(col, val) {
  if (col === 'enabled') return val ? 1 : 0;
  if (col === 'cue_actions' || col === 'vmix_actions') return JSON.stringify(val ?? []);
  if (col === 'singular_action') return val == null ? null : JSON.stringify(val);
  return val ?? null;
}

const CUE_COLUMNS = [
  'show_id', 'order', 'label', 'cue_type', 'status', 'planned_duration',
  'actual_duration', 'script', 'top_right_logo', 'bottom_right_logo',
  'other_graphics', 'notes', 'colour', 'enabled', 'parent_id', 'cue_actions',
  'loop_mode', 'vmix_actions', 'singular_action', 'last_edited_by',
  'created_at', 'updated_at', 'deleted_at',
];

const q = (c) => `"${c}"`; // quote identifiers ("order" is a reserved word)

// ── Reads ─────────────────────────────────────────────────────────────────

export async function listCues(showId) {
  const db = getPowerSync();
  const rows = await db.getAll(
    `SELECT * FROM cues WHERE show_id = ? AND deleted_at IS NULL ORDER BY ${q('order')} ASC`,
    [showId]
  );
  return { data: rows.map(rowToCue) };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const withTimeout = (p, ms) => Promise.race([
  Promise.resolve(p).catch(() => {}),
  new Promise((r) => setTimeout(r, ms)),
]);

export async function getShowWithCues(showId, { localWaitMs = 4000, hardCapMs = 8000 } = {}) {
  const db = getPowerSync();
  const readShow = () => db.getOptional(`SELECT * FROM shows WHERE id = ? AND deleted_at IS NULL`, [showId]);

  // Not in the local store yet — could be replication lag (a just-created/cloned
  // show), an initial sync still running, or the local DB being slow/stuck.
  // Wait briefly, but NEVER hang: cap waitForFirstSync, then poll for a few
  // seconds. If it still isn't here, return null → local miss → cloud fallback.
  const read = (async () => {
    let show = await readShow();
    if (!show) {
      await withTimeout(db.waitForFirstSync(), 2000);
      const deadline = Date.now() + localWaitMs;
      while (!show && Date.now() < deadline) {
        await sleep(300);
        show = await readShow();
      }
    }
    if (!show) return null;
    const { data: cues } = await listCues(showId);
    return { data: { ...show, cues } };
  })();

  // A wedged local store (e.g. OPFS/IndexedDB left locked or half-written after
  // a hard crash) can make even a simple query hang WITHOUT ever rejecting —
  // which would pin the editor on "Loading…" forever, since the localMiss →
  // cloud fallback only fires on a rejection. Race the whole local read against
  // a hard cap; on timeout, treat it as a local miss and fall back to the cloud.
  const TIMEOUT = Symbol('timeout');
  const result = await Promise.race([
    read,
    new Promise((resolve) => setTimeout(() => resolve(TIMEOUT), hardCapMs)),
  ]);

  if (result === TIMEOUT || result === null) {
    const e = new Error(result === TIMEOUT ? 'local store read timed out' : 'show not in local store yet');
    e.localMiss = true;
    throw e;
  }

  return result;
}

/** Reactively watch a show's cues. cb receives the API-shaped cue array.
 *  Returns an unsubscribe function. */
export function watchCues(showId, cb) {
  const db = getPowerSync();
  const controller = new AbortController();
  db.watch(
    `SELECT * FROM cues WHERE show_id = ? AND deleted_at IS NULL ORDER BY ${q('order')} ASC`,
    [showId],
    { onResult: (res) => cb((res.rows?._array ?? []).map(rowToCue)) },
    { signal: controller.signal }
  );
  return () => controller.abort();
}

/** Reactively watch a show's OWN row (name, scheduled_start, status, …) — the
 *  cues are watched separately by watchCues. Without this the editor read the
 *  show once on load, so edits made on another machine (e.g. the start time)
 *  only appeared after a manual refresh. cb receives the show row, or null if
 *  it isn't in the local store. Returns an unsubscribe function. */
export function watchShow(showId, cb) {
  const db = getPowerSync();
  const controller = new AbortController();
  db.watch(
    `SELECT * FROM shows WHERE id = ? AND deleted_at IS NULL`,
    [showId],
    { onResult: (res) => cb(res.rows?._array?.[0] ?? null) },
    { signal: controller.signal }
  );
  return () => controller.abort();
}

// ── Writes ──────────────────────────────────────────────────────────────────

export async function createCue(showId, data = {}) {
  const db = getPowerSync();
  const id = crypto.randomUUID();
  const now = new Date().toISOString();

  // An explicit `order` means "insert AT this position" — everything already at
  // or after it shifts down by one. Mirrors the REST handler, which does the
  // same shift. Without this the local path always appended, so a mid-rundown
  // insert (the row-level + affordance, or Insert-below-selection) silently
  // landed at the bottom of the rundown while still taking its parent_id —
  // leaving a cue that collapsed with its group but sat nowhere near it.
  const explicitOrder = Number.isInteger(data.order) ? data.order : null;
  let order = explicitOrder;
  if (order === null) {
    const max = await db.getOptional(
      `SELECT MAX(${q('order')}) as m FROM cues WHERE show_id = ? AND deleted_at IS NULL`, [showId]
    );
    order = (max?.m ?? -1) + 1;
  }

  const cue = {
    show_id: showId, order,
    label: data.label ?? 'New Cue',
    cue_type: data.cue_type ?? 'SEGMENT',
    status: 'pending',
    planned_duration: data.planned_duration ?? 60,
    actual_duration: null,
    script: data.script ?? null,
    top_right_logo: data.top_right_logo ?? null,
    bottom_right_logo: data.bottom_right_logo ?? null,
    other_graphics: data.other_graphics ?? null,
    notes: data.notes ?? null,
    colour: data.colour ?? null,
    enabled: data.enabled === false ? 0 : 1,
    parent_id: data.parent_id ?? null,
    cue_actions: toCol('cue_actions', data.cue_actions),
    loop_mode: data.loop_mode ?? 'off',
    vmix_actions: toCol('vmix_actions', data.vmix_actions),
    singular_action: toCol('singular_action', data.singular_action),
    last_edited_by: data.last_edited_by ?? null,
    created_at: now, updated_at: now, deleted_at: null,
  };

  await db.writeTransaction(async (tx) => {
    // Shift strictly after the anchor cue (the one we're inserting below), i.e.
    // everything currently at `order` or beyond. Must precede the INSERT so the
    // slot is free.
    if (explicitOrder !== null) {
      await shiftOrdersAfter(tx, showId, explicitOrder - 1, 1, now);
    }
    await insertCueRow(tx, { id, ...cue });
  });
  return { data: rowToCue({ id, ...cue }) };
}

export async function updateCue(showId, cueId, data = {}) {
  const db = getPowerSync();
  const cols = Object.keys(data).filter((c) => CUE_COLUMNS.includes(c) && c !== 'deleted_at');
  cols.push('updated_at');
  const values = cols.map((c) => (c === 'updated_at' ? new Date().toISOString() : toCol(c, data[c])));

  await db.execute(
    `UPDATE cues SET ${cols.map((c) => `${q(c)} = ?`).join(', ')} WHERE id = ? AND show_id = ?`,
    [...values, cueId, showId]
  );
  const row = await db.getOptional(`SELECT * FROM cues WHERE id = ?`, [cueId]);
  return { data: rowToCue(row) };
}

export async function deleteCue(showId, cueId) {
  const db = getPowerSync();
  const now = new Date().toISOString();
  await db.writeTransaction(async (tx) => {
    // GROUP cues take their children with them.
    await tx.execute(
      `UPDATE cues SET deleted_at = ?, updated_at = ? WHERE parent_id = ? AND show_id = ?`,
      [now, now, cueId, showId]
    );
    await tx.execute(
      `UPDATE cues SET deleted_at = ?, updated_at = ? WHERE id = ? AND show_id = ?`,
      [now, now, cueId, showId]
    );
  });
  return { data: {} };
}

export async function reorderCues(showId, orderedIds) {
  const db = getPowerSync();
  const now = new Date().toISOString();
  await db.writeTransaction(async (tx) => {
    for (let i = 0; i < orderedIds.length; i++) {
      await tx.execute(
        `UPDATE cues SET ${q('order')} = ?, updated_at = ? WHERE id = ? AND show_id = ?`,
        [i, now, orderedIds[i], showId]
      );
    }
  });
  return { data: { orderedIds } };
}

async function shiftOrdersAfter(tx, showId, afterOrder, by, now) {
  await tx.execute(
    `UPDATE cues SET ${q('order')} = ${q('order')} + ?, updated_at = ?
     WHERE show_id = ? AND deleted_at IS NULL AND ${q('order')} > ?`,
    [by, now, showId, afterOrder]
  );
}

// Insert a full cue row. `row` holds raw DB-format values (it comes from a
// SELECT *), so values pass straight through. `row.id` is the new id.
async function insertCueRow(tx, row) {
  await tx.execute(
    `INSERT INTO cues (id, ${CUE_COLUMNS.map(q).join(', ')})
     VALUES (?, ${CUE_COLUMNS.map(() => '?').join(', ')})`,
    [row.id, ...CUE_COLUMNS.map((c) => row[c])]
  );
}

export async function cloneCue(showId, cueId) {
  const db = getPowerSync();
  const src = await db.getOptional(`SELECT * FROM cues WHERE id = ? AND show_id = ? AND deleted_at IS NULL`, [cueId, showId]);
  if (!src) { const e = new Error('Cue not found'); e.response = { status: 404 }; throw e; }
  const now = new Date().toISOString();
  const newId = crypto.randomUUID();

  // A GROUP cue takes its children with it — clone them too, remapping their
  // parent_id to the new group so the clone is actually grouped.
  const children = src.cue_type === 'GROUP'
    ? await db.getAll(`SELECT * FROM cues WHERE parent_id = ? AND show_id = ? AND deleted_at IS NULL ORDER BY ${q('order')} ASC`, [cueId, showId])
    : [];

  // Anchor the clone AFTER the group's whole block (the group row PLUS its
  // children), not just after the group row. Otherwise the original children —
  // which sit between the group row and the next cue — get shifted below the
  // cloned children, so the rundown reads 7, 8, 8.1–8.5, 7.1–7.5 and looks like
  // everything lives inside the clone.
  const anchorOrder = children.reduce((m, c) => Math.max(m, c.order), src.order);

  await db.writeTransaction(async (tx) => {
    await shiftOrdersAfter(tx, showId, anchorOrder, 1 + children.length, now);
    await insertCueRow(tx, { ...src, id: newId, order: anchorOrder + 1, status: 'pending', actual_duration: null, created_at: now, updated_at: now, deleted_at: null });
    for (let i = 0; i < children.length; i++) {
      await insertCueRow(tx, { ...children[i], id: crypto.randomUUID(), order: anchorOrder + 2 + i, parent_id: newId, status: 'pending', actual_duration: null, created_at: now, updated_at: now, deleted_at: null });
    }
  });

  const row = await db.getOptional(`SELECT * FROM cues WHERE id = ?`, [newId]);
  return { data: rowToCue(row) };
}

export async function bulkCloneCues(showId, cueIds, insertAfterId) {
  const db = getPowerSync();
  const now = new Date().toISOString();
  // Pre-assign new ids so a cloned child's parent_id can be remapped to its
  // cloned parent (when the parent is part of the same selection).
  const idMap = new Map(cueIds.map((id) => [id, crypto.randomUUID()]));
  const created = [];
  await db.writeTransaction(async (tx) => {
    // Clone in RUNDOWN order, not selection/click order, so a GROUP lands before
    // its children and the hierarchical numbering (e.g. 5, 5.1, 5.2) comes out
    // right. Children whose parent GROUP is also in the selection are re-parented
    // to the cloned group via idMap; a child cloned without its parent becomes
    // top-level (idMap miss → null).
    const srcRows = [];
    for (const srcId of cueIds) {
      const src = await tx.get(`SELECT * FROM cues WHERE id = ?`, [srcId]);
      if (src) srcRows.push(src);
    }
    srcRows.sort((a, b) => a.order - b.order);

    const anchor = insertAfterId
      ? await tx.get(`SELECT ${q('order')} as o FROM cues WHERE id = ?`, [insertAfterId])
      : await tx.get(`SELECT MAX(${q('order')}) as o FROM cues WHERE show_id = ? AND deleted_at IS NULL`, [showId]);
    let at = anchor?.o ?? -1;
    await shiftOrdersAfter(tx, showId, at, srcRows.length, now);
    for (const src of srcRows) {
      at += 1;
      const newParent = src.parent_id ? (idMap.get(src.parent_id) ?? null) : null;
      await insertCueRow(tx, { ...src, id: idMap.get(src.id), order: at, parent_id: newParent, status: 'pending', actual_duration: null, created_at: now, updated_at: now, deleted_at: null });
      created.push(idMap.get(src.id));
    }
  });
  return { data: { createdIds: created } };
}
